import { axiosInstance } from '@/Api/axiosV2Config';

/** Un compte Stripe supervisable par l'utilisateur (résolu serveur, cf. viewableAccounts). */
export interface ViewableStripeAccount {
  societyId: string;
  societyName: string;
  stripeAccountId: string;
  stripeChargesEnabled: boolean | null;
  stripePayoutsEnabled: boolean | null;
}

export interface SelfAccountSessionResponse {
  success: boolean;
  clientSecret?: string;
  publishableKey?: string;
  error?: string;
}

/** Frais de plateforme Affinicar (markup facturé au vendeur) pour un paiement — TTC. */
export interface PlatformFeeItem {
  id: string;
  /** date ISO */
  date: string;
  contractNum: string;
  echeanceNumber: number | null;
  amount: number;
  isUpsell: boolean;
  invoiced: boolean;
  invoiceLabel: string | null;
  chargeId: string;
}

/**
 * Mode d'accès au dashboard :
 *  - `self`  : vendeur sur son propre compte (endpoints `/stripe/self/*`, lecture
 *              seule, scopés propriété/portée serveur) ;
 *  - `admin` : administrateur consultant un vendeur choisi (endpoints
 *              `/stripe/admin/*`, actions activées, gardés par `stripe.account`).
 */
export type StripeDashboardMode = 'self' | 'admin';

/** Un payout listé pour le sélecteur d'export du rapprochement. */
export interface PayoutSummary {
  id: string;
  amount: number;
  currency: string;
  /** unix (secondes) */
  arrivalDate: number | null;
  status: string;
}

/** Paramètres d'export : un payout précis, OU une période (ISO). */
export interface PayoutReconciliationParams {
  payoutId?: string;
  periodStart?: string;
  periodEnd?: string;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function extractFilename(response: any, fallback: string): string {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] || fallback;
}

export const stripeAdminApi = {
  /**
   * Comptes Stripe que l'utilisateur courant a le droit de superviser.
   * Source unique du sélecteur (le filtre propriété/portée est fait serveur).
   * Spécifique au self : côté admin la société est choisie via le vendeur ouvert.
   */
  getViewableAccounts: async (): Promise<ViewableStripeAccount[]> => {
    const { data } = await axiosInstance.get<ViewableStripeAccount[]>('/stripe/self/accounts');
    return data;
  },

  /**
   * Crée une Account Session pour la société demandée.
   * Sert de `fetchClientSecret` (rappelé à chaque (ré)initialisation Connect).
   * `self` → session lecture seule ; `admin` → session avec actions.
   */
  createAccountSession: async (
    societyId: string,
    mode: StripeDashboardMode = 'self',
  ): Promise<SelfAccountSessionResponse> => {
    const url = mode === 'admin' ? '/stripe/admin/account-session' : '/stripe/self/account-session';
    const { data } = await axiosInstance.post<SelfAccountSessionResponse>(url, { societyId });
    return data;
  },

  /** Frais de plateforme facturés au vendeur pour la société. */
  getPlatformFees: async (
    societyId: string,
    mode: StripeDashboardMode = 'self',
  ): Promise<PlatformFeeItem[]> => {
    const url = mode === 'admin' ? '/stripe/admin/platform-fees' : '/stripe/self/platform-fees';
    const { data } = await axiosInstance.get<PlatformFeeItem[]>(url, {
      params: { societyId },
    });
    return data;
  },

  /** Payouts récents de la société (pour le sélecteur d'export). */
  getPayouts: async (
    societyId: string,
    mode: StripeDashboardMode = 'self',
  ): Promise<PayoutSummary[]> => {
    const url = mode === 'admin' ? '/stripe/admin/payouts' : '/stripe/self/payouts';
    const { data } = await axiosInstance.get<PayoutSummary[]>(url, {
      params: { societyId },
    });
    return data;
  },

  /**
   * Exporte le CSV de rapprochement (un payout, ou une période) et déclenche le
   * téléchargement. Sur erreur (ex. période trop large), le message serveur est
   * renvoyé en blob → on le relit en texte pour le remonter proprement.
   */
  exportPayoutReconciliation: async (
    societyId: string,
    params: PayoutReconciliationParams,
    mode: StripeDashboardMode = 'self',
  ): Promise<void> => {
    const url =
      mode === 'admin'
        ? '/stripe/admin/payout-reconciliation'
        : '/stripe/self/payout-reconciliation';
    try {
      const response = await axiosInstance.post(
        url,
        { societyId, ...params },
        { responseType: 'blob' },
      );
      const filename = extractFilename(response, 'rapprochement.csv');
      triggerDownload(new Blob([response.data]), filename);
    } catch (err: any) {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text().catch(() => '');
        let message = text || 'Export impossible';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          /* pas du JSON → on garde le texte brut */
        }
        throw new Error(message);
      }
      throw err;
    }
  },
};
